// The Settings sections — the single declaration everything else reads.
//
// §9 named eight. **People** is the ninth, added 2026-10-29 under §9.1's own
// "unless a real need emerges" clause — adding a person existed only on the
// v1 `/operators` page and had no home in the v2 shell. `surfaces/settings.md`
// carries the reason. Everything below applies to it unchanged.
//
// WHY THIS LIST LIVES HERE AND NOT IN `areas.js`
// ==============================================
// `surfaces/settings.md` line 9 offers two shapes for these sections, in its
// own words: "Sections (Y-axis within Settings **or a settings X-axis**)".
// Both are sanctioned. This build takes the first.
//
// The reason is concrete rather than aesthetic. `areas.js` declares four
// Settings tabs — "", offers, system, infrastructure — and §9 names eight. The
// four with no declared tab are My Executive Assistant, Connections, Models and
// Capabilities. `areas.js` is frozen for this night with no owner, so the four
// missing entries cannot be added to the X-axis without editing a shared file
// that five sessions are working around.
//
// Rather than ship four sections reachable only by typing a URL, the section
// index moves inside the surface, where §9's first shape puts it. The X-axis
// keeps its four entries and they now behave as deep links into this index:
// every tab in `areas.js` maps to a section below, and every section is
// reachable by clicking from any other. Nothing frozen moves, and the optional
// morning cleanup is four lines in `areas.js` — after which the X-axis and this
// index simply agree, and neither has to change again.
//
// FOUNDER GATING IS §9's, WHICH IS WIDER THAN `areas.js`'s
// ========================================================
// `areas.js` marks two Settings tabs founder-only. §9 marks four sections
// founder/admin: Models, Capabilities, Infrastructure and System Configuration.
// §9 wins, so Models and Capabilities are gated here even though the X-axis has
// no entry for them to gate.
//
// As everywhere else in the shell (R5), this decides what an operator SEES and
// nothing about what they can REACH. Every founder-only endpoint behind these
// sections is refused by `require_founder` on the server; a non-founder who
// types the URL gets the section frame and a refusal from the API, which is the
// honest outcome. `display: none` is not access control and neither is this.

/**
 * The sections, in §9's order.
 *
 * `tab`     the URL segment under /v2/settings. "" is the area home.
 * `inXNav`  true when `areas.js` already declares this tab, so the X-axis
 *           links to it too. Informational — nothing branches on it except the
 *           test that keeps the two files honest.
 */
export const SECTIONS = [
  {
    id: "me",
    tab: "",
    label: "My Settings",
    blurb: "Your profile, how Cipher notifies you, and the tour.",
    founderOnly: false,
    inXNav: true,
  },
  {
    id: "ea",
    tab: "ea",
    label: "My Executive Assistant",
    blurb: "Links into Customize EA. Not a second copy of it.",
    founderOnly: false,
    inXNav: false,
  },
  {
    id: "offers",
    tab: "offers",
    label: "Offers",
    blurb: "Offers, campaigns, and the reference data they hang off.",
    founderOnly: false,
    inXNav: true,
  },
  {
    id: "connections",
    tab: "connections",
    label: "Connections",
    blurb: "Accounts, credentials and IDs for external systems.",
    founderOnly: false,
    inXNav: false,
  },
  {
    id: "models",
    tab: "models",
    label: "Models",
    blurb: "Providers, routing and local-vs-API configuration.",
    founderOnly: true,
    inXNav: false,
  },
  {
    id: "capabilities",
    tab: "capabilities",
    label: "Capabilities",
    blurb: "What Cipher can do, and where each capability is used.",
    founderOnly: true,
    inXNav: false,
  },
  {
    id: "infrastructure",
    tab: "infrastructure",
    label: "Infrastructure",
    blurb: "Integration and scheduler health, and what it is costing.",
    founderOnly: true,
    inXNav: true,
  },
  {
    id: "system",
    tab: "system",
    label: "System Configuration",
    blurb: "Governance, audit and advanced configuration.",
    founderOnly: true,
    inXNav: true,
  },
  // Ninth, and the only one §9 did not name. §9.1 reserved "no generic
  // user-invitation / role-management admin **unless a real need emerges**",
  // and on 2026-10-29 one did: adding a person was reachable only from the v1
  // `/operators` page, so from inside the v2 shell there was no way to do it
  // at all. `surfaces/settings.md` records the condition being met.
  // Founder-gated because every endpoint behind it is `require_founder`.
  {
    id: "people",
    tab: "people",
    label: "People",
    blurb: "Add someone to Cipher, and see who is already in.",
    founderOnly: true,
    inXNav: false,
  },
];

/** True when the authenticated operator is the Founder. Never a name (C5). */
export function isFounder(user) {
  return user?.role === "FOUNDER";
}

/** The sections this operator may see. */
export function visibleSections(user) {
  const founder = isFounder(user);
  return SECTIONS.filter((s) => !s.founderOnly || founder);
}

/** The section a URL tab resolves to, or the home section. */
export function sectionForTab(tab) {
  return SECTIONS.find((s) => s.tab === (tab || "")) || SECTIONS[0];
}

/** The route for a section, for the index's links. */
export function pathFor(section) {
  return section.tab ? `/v2/settings/${section.tab}` : "/v2/settings";
}
